/**
 * NIP-10 Reply Service
 * Handles threaded replies following NIP-10 specification
 * 
 * NIP-10 Tag Markers:
 * - root: The root event (original entry)
 * - reply: The event being replied to
 * - mention: A quoted/mentioned event
 */

import { Event, finalizeEvent } from 'nostr-tools'
import { getNostrPool } from '../lib/nostrPool'
import { DEFAULT_RELAYS } from '../config/constants'

interface ReplyParams {
  content: string
  replyToEvent: Event
  rootEvent?: Event // Optional: if replying to a reply, this is the original entry
  sk?: Uint8Array | null
}

/**
 * Create a NIP-10 compliant reply event
 */
export async function createReply({
  content,
  replyToEvent,
  rootEvent,
  sk
}: ReplyParams): Promise<{ success: boolean; eventId?: string; error?: string }> {
  try {
    const pool = getNostrPool()

    // Determine the root event (original entry)
    const actualRoot = rootEvent || replyToEvent
    const isDirectReply = !rootEvent // Replying directly to root

    // Build NIP-10 compliant tags
    const tags: string[][] = []

    // Always add root tag
    tags.push(['e', actualRoot.id, '', 'root'])

    // If replying to a reply (not the root), add reply tag
    if (!isDirectReply) {
      tags.push(['e', replyToEvent.id, '', 'reply'])
    }

    // Add p-tag for the author being replied to
    tags.push(['p', replyToEvent.pubkey])

    // If root author is different, also tag them
    if (rootEvent && rootEvent.pubkey !== replyToEvent.pubkey) {
      tags.push(['p', rootEvent.pubkey])
    }

    // Preserve topic hashtags from root
    const topicTags = actualRoot.tags.filter(t => t[0] === 't')
    topicTags.forEach(t => tags.push([...t]))

    // Add language tag
    tags.push(['l', 'tr', 'ISO-639-1'])

    // Create the event template
    const eventTemplate = {
      kind: 1,
      created_at: Math.floor(Date.now() / 1000),
      tags,
      content: formatReplyContent(content)
    }

    let signedEvent: Event

    if (sk) {
      // Sign with provided secret key (already Uint8Array)
      signedEvent = finalizeEvent(eventTemplate, sk) as Event
    } else if (window.nostr) {
      // Sign with NIP-07 extension
      signedEvent = await window.nostr.signEvent(eventTemplate) as Event
    } else {
      return { success: false, error: 'Yanıt göndermek için giriş yapmalısınız.' }
    }

    // Publish to relays
    await Promise.allSettled(
      pool.publish(DEFAULT_RELAYS, signedEvent)
    )

    console.log('📝 Reply published:', signedEvent.id)

    return { success: true, eventId: signedEvent.id }
  } catch (error) {
    console.error('❌ Reply creation failed:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Yanıt gönderilemedi'
    }
  }
}

/**
 * Format reply content with Mavililer styling
 */
function formatReplyContent(content: string): string {
  // Add mavililer function formatReplyContent(content: string): string {
  const tags = '#turkstr #turkce #turkish #mavililer'
  if (content.includes('#turkstr')) {
    return content
  }
  return `${content.trim()}\n\n${tags}`
}

/**
 * Get replies to a specific event
 */
export async function getReplies(eventId: string): Promise<Event[]> {
  const pool = getNostrPool()

  return new Promise((resolve) => {
    const replies: Event[] = []

    const filter = {
      kinds: [1],
      '#e': [eventId],
      limit: 50
    }

    const sub = pool.subscribeMany(DEFAULT_RELAYS, filter, {
      onevent(event: Event) {
        // Check if this is a proper NIP-10 reply (has root or reply marker)
        const eTags = event.tags.filter(t => t[0] === 'e')
        const isReply = eTags.some(t =>
          t[1] === eventId && (t[3] === 'root' || t[3] === 'reply')
        ) || eTags.some(t => t[1] === eventId && eTags.length === 1) // Legacy: single e-tag is a reply

        if (isReply || eTags.some(t => t[1] === eventId)) {
          replies.push(event)
        }
      },
      oneose() {
        // Sort by created_at ascending (oldest first for thread order)
        replies.sort((a, b) => a.created_at - b.created_at)
        resolve(replies)
        sub.close()
      }
    }
    )

    // Timeout after 5 seconds
    setTimeout(() => {
      sub.close()
      resolve(replies)
    }, 5000)
  })
}

/**
 * Check if an event is a reply to another event
 */
export function isReplyTo(event: Event, targetEventId: string): boolean {
  const eTags = event.tags.filter(t => t[0] === 'e')
  return eTags.some(t => t[1] === targetEventId)
}

/**
 * Get the root event ID from a reply
 */
export function getRootEventId(event: Event): string | null {
  const rootTag = event.tags.find(t => t[0] === 'e' && t[3] === 'root')
  if (rootTag) return rootTag[1]

  // Legacy: first e-tag is often the root
  const eTags = event.tags.filter(t => t[0] === 'e')
  return eTags[0]?.[1] || null
}

/**
 * Get the immediate parent event ID (what this is replying to)
 */
export function getReplyToId(event: Event): string | null {
  // Check for explicit reply marker
  const replyTag = event.tags.find(t => t[0] === 'e' && t[3] === 'reply')
  if (replyTag) return replyTag[1]

  // Check for root (means direct reply to root)
  const rootTag = event.tags.find(t => t[0] === 'e' && t[3] === 'root')
  if (rootTag) return rootTag[1]

  // Legacy: last e-tag is often what's being replied to
  const eTags = event.tags.filter(t => t[0] === 'e')
  return eTags[eTags.length - 1]?.[1] || null
}
